import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { PencilSquareIcon } from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import { IPart } from "../../../models/Part";
import classNames from "../../../utils/classNames";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import EditListingSpecifics from "../EditListingSpecifics";
import RecentlyListedErrorCell from "../recentlyListed/RecentlyListedErrorCell";
import {
  getListingWorkflowSteps,
  loadWorkflowOverrides,
  saveWorkflowOverrides,
  type ListingWorkflowStep,
} from "./listingWorkflowStatus";

function useWorkflowOverrides(partId: string) {
  const [overrides, setOverrides] = useState<Record<string, boolean>>(() =>
    loadWorkflowOverrides(partId)
  );

  useEffect(() => {
    setOverrides(loadWorkflowOverrides(partId));
  }, [partId]);

  const persist = useCallback(
    (next: Record<string, boolean>) => {
      setOverrides(next);
      saveWorkflowOverrides(partId, next);
    },
    [partId]
  );

  return { overrides, persist };
}

function displayDone(
  step: ListingWorkflowStep,
  overrides: Record<string, boolean>
) {
  return Object.prototype.hasOwnProperty.call(overrides, step.id)
    ? Boolean(overrides[step.id])
    : step.done;
}

type Props = {
  part: IPart;
  onListNow?: (part: IPart) => void;
};

export default function UnlistedListingWorkflowCell({ part, onListNow }: Props) {
  const steps = useMemo(() => getListingWorkflowSteps(part), [part]);
  const { overrides, persist } = useWorkflowOverrides(part.id);
  const [specificsOpen, setSpecificsOpen] = useState(false);

  const handleToggle = useCallback(
    (step: ListingWorkflowStep) => {
      const current = displayDone(step, overrides);
      const next = !current;
      const newOverrides: Record<string, boolean> = { ...overrides };
      if (next === step.done) {
        delete newOverrides[step.id];
      } else {
        newOverrides[step.id] = next;
      }
      persist(newOverrides);
    },
    [overrides, persist]
  );

  return (
    <div
      className="flex h-full w-full min-w-0 flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <Modal
        key={`aspects-workflow-${part.id}`}
        open={specificsOpen}
        setOpen={setSpecificsOpen}
        width="max-w-7xl"
      >
        {specificsOpen && <EditListingSpecifics part={part} />}
      </Modal>
      <div className="flex h-full flex-col rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/95 to-white px-3 py-2.5 shadow-sm ring-1 ring-slate-200/50">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          Listing workflow
        </p>
        {part.batchItem && (
          <div className="mb-2 border-b border-amber-200 pb-2">
            <RecentlyListedErrorCell
              batchItem={part.batchItem}
              showExtraInfo={false}
            />
          </div>
        )}

        <ul
          className="flex w-full min-w-0 flex-col gap-0.5"
          aria-label="Listing workflow checklist"
        >
        {steps.map((step) => {
          const done = displayDone(step, overrides);

          if (step.id === "ready") {
            return (
              <li
                key={step.id}
                className="mt-1 border-t border-slate-200 pt-1.5"
              >
                <div className="flex flex-col gap-1.5 rounded-lg p-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    {step.label}
                  </p>
                  <div className="flex flex-col items-stretch gap-1.5 sm:flex-row sm:items-stretch">
                    <button
                      type="button"
                      onClick={() => handleToggle(step)}
                      title={!done && step.detail ? step.detail : undefined}
                      aria-pressed={done}
                      aria-label={
                        !done && step.detail
                          ? `Not ready. ${step.detail} Click to override.`
                          : `${done ? "Ready" : "Not ready"}. Click to change override.`
                      }
                      className="flex min-h-[1.75rem] flex-1 flex-col items-stretch justify-center gap-1 rounded-md text-left transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:ring-offset-1"
                    >
                      <span
                        className={classNames(
                          "inline-flex w-full max-w-full items-center justify-center rounded-full px-2.5 py-1 text-[13px] font-semibold shadow-sm ring-1 ring-inset",
                          done
                            ? "bg-emerald-100 text-emerald-800 ring-emerald-200/80"
                            : "bg-amber-50 text-amber-900 ring-amber-200/90"
                        )}
                      >
                        {done ? "Ready" : "Not ready"}
                      </span>
                      {step.detail && !done && (
                        <span className="line-clamp-3 text-left text-xs leading-snug text-amber-900/90">
                          {step.detail}
                        </span>
                      )}
                    </button>
                    {onListNow && (
                      <div className="shrink-0 self-center sm:self-stretch sm:flex sm:items-stretch">
                        <ButtonInput
                          label="List now"
                          isSubmit={false}
                          onClick={() => onListNow(part)}
                          classes="!h-full !min-h-[1.75rem] !px-2.5 !py-1 !text-[13px] !font-semibold !whitespace-nowrap w-full sm:w-auto"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          }

          return (
            <li key={step.id} className="flex items-start gap-1">
              <button
                type="button"
                onClick={() => handleToggle(step)}
                title={!done && step.detail ? step.detail : step.label}
                aria-pressed={done}
                aria-label={
                  !done && step.detail
                    ? `${step.label}: not ticked. ${step.detail} Click to override.`
                    : `${step.label}: ${done ? "ticked" : "not ticked"}. Click to change.`
                }
                className={classNames(
                  "flex min-w-0 flex-1 items-start gap-1.5 rounded-md px-1 py-0.5 text-left transition",
                  "hover:bg-slate-100/90 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                )}
              >
                {done ? (
                  <CheckCircleIcon
                    className="mt-px h-4 w-4 shrink-0 text-emerald-600"
                    aria-hidden
                  />
                ) : (
                  <span
                    className="mt-px h-4 w-4 shrink-0 rounded-full border-[1.5px] border-slate-300 bg-white"
                    aria-hidden
                  />
                )}
                <span className="flex min-w-0 flex-col">
                  <span
                    className={classNames(
                      "min-w-0 truncate text-[13px] leading-snug",
                      done ? "text-slate-900" : "text-slate-600"
                    )}
                  >
                    {step.label}
                  </span>
                  {!done && step.detail && (
                    <span className="line-clamp-2 text-[11px] leading-snug text-amber-800/90">
                      {step.detail}
                    </span>
                  )}
                </span>
              </button>
              {step.id === "specifics" && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSpecificsOpen(true);
                  }}
                  title="Edit item specifics"
                  aria-label="Edit item specifics"
                  className="mt-px inline-flex h-6 w-6 flex-none items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                >
                  <PencilSquareIcon className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
            </li>
          );
        })}
        </ul>
      </div>
    </div>
  );
}
